/**
 * Payment success landing page.
 *
 * After Stripe hosted-checkout the browser lands here with
 *   ?session_id=cs_test_...
 * If a valid session is present (user still signed in), we poll
 * /api/paywall/checkout/status/{session_id} every 2s (up to 30s) until
 * payment_status flips to "paid". If the webhook lands first, the first
 * poll returns paid immediately. If the status endpoint (which itself
 * falls back to Stripe.retrieve) is what unlocks it, the row is flipped
 * inline.
 *
 * If the browser session lost its JWT during the Stripe roundtrip
 * (fresh browser, cookie eviction, expired token), we render an
 * honest "you returned from Stripe checkout — sign back in to see
 * confirmation" panel and route the user through /login while
 * preserving the ?session_id query string in `state.from`, so the
 * poll can resume after re-auth.
 *
 * No side effects — the entitlement grant is created by the webhook
 * (server-side, signature-verified) or by the status-endpoint inline
 * fallback (auth-required, own-user-scoped). This page only reflects.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/lib/auth";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, AlertCircle, LogIn } from "lucide-react";


function formatUSD(cents, currency = "USD") {
  if (cents == null) return "";
  return `${currency === "USD" ? "$" : `${currency} `}${(cents / 100).toFixed(2)}`;
}


export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "loading", data: null, error: null });

  // Full return path (path + query string), used when the unauth
  // branch has to bounce the user through /login and come back here
  // with the session_id preserved.
  const returnPath = `${location.pathname}${location.search}`;

  useEffect(() => {
    // Don't start polling while auth is resolving, and don't poll if
    // the user isn't signed in — the status endpoint is auth-only.
    if (authLoading || !user) return;
    if (!sessionId) {
      setState({ status: "error", error: "No session id in URL.", data: null });
      return;
    }
    let cancelled = false;
    let attempts = 0;

    async function poll() {
      attempts += 1;
      try {
        const res = await api.get(`/paywall/checkout/status/${sessionId}`);
        if (cancelled) return;
        setState({ status: "loaded", data: res.data, error: null });
        if (res.data?.payment_status === "paid") return;
        if (attempts < 15) setTimeout(poll, 2000);
      } catch (err) {
        if (cancelled) return;
        setState({
          status: "error",
          error: extractApiError(err, "Couldn't confirm your payment."),
          data: null,
        });
      }
    }

    poll();
    return () => { cancelled = true; };
  }, [authLoading, user, sessionId]);

  // ---- Unauthenticated return branch ---------------------------------
  // Do NOT claim payment succeeded here. Stripe's success_url only
  // fires on a paid session, but we still can't confirm it until the
  // authenticated status poll runs, and the entitlement is granted
  // server-side by the webhook regardless of what this page shows.
  if (!authLoading && !user) {
    return (
      <AppShell>
        <div
          data-testid="payment-success-page"
          className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-12 sm:py-20 text-center"
        >
          <LogIn className="h-8 w-8 text-fg-secondary mx-auto" aria-hidden="true" />
          <h1
            data-testid="payment-success-signin-title"
            className="mt-4 text-2xl font-heading text-fg-primary"
          >
            You&rsquo;ve returned from Stripe checkout
          </h1>
          <p
            data-testid="payment-success-signin-body"
            className="mt-3 text-sm text-fg-secondary max-w-md mx-auto"
          >
            Your session ended during the payment step. Sign back in and
            we&rsquo;ll finish confirming your payment and show your updated
            access. Your entitlement is stored on your account, not on
            this browser.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Link
              to="/login"
              state={{ from: returnPath }}
            >
              <Button data-testid="payment-success-signin-btn">
                Sign in to confirm
              </Button>
            </Link>
            <Link to="/plans">
              <Button
                variant="outline"
                data-testid="payment-success-plans-btn"
              >
                Back to plans
              </Button>
            </Link>
          </div>
          {sessionId ? (
            <p className="mt-6 text-xs text-fg-muted">
              Session <code className="text-xs">{sessionId.slice(0, 24)}&hellip;</code>
            </p>
          ) : null}
        </div>
      </AppShell>
    );
  }

  const paid = state.data?.payment_status === "paid";

  return (
    <AppShell>
      <div
        data-testid="payment-success-page"
        className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-12 sm:py-20 text-center"
      >
        {state.status === "loading" || (state.status === "loaded" && !paid) ? (
          <>
            <Loader2 className="h-8 w-8 text-fg-secondary animate-spin mx-auto" aria-hidden="true" />
            <h1
              data-testid="payment-success-pending-title"
              className="mt-4 text-2xl font-heading text-fg-primary"
            >
              Confirming your payment&hellip;
            </h1>
            <p className="mt-2 text-sm text-fg-secondary">
              Stripe is delivering the receipt to us. This usually finishes in
              a second or two. You don't need to refresh.
            </p>
          </>
        ) : state.status === "error" ? (
          <>
            <AlertCircle className="h-8 w-8 text-red-400 mx-auto" aria-hidden="true" />
            <h1 className="mt-4 text-2xl font-heading text-fg-primary">
              Something went wrong
            </h1>
            <Alert variant="destructive" className="mt-4">
              <AlertDescription data-testid="payment-success-error">{state.error}</AlertDescription>
            </Alert>
            <Link to="/plans">
              <Button variant="outline" className="mt-6"
                       data-testid="payment-success-back-to-plans-btn">
                Back to plans
              </Button>
            </Link>
          </>
        ) : (
          <>
            <CheckCircle2 className="h-10 w-10 text-progress-teal mx-auto" aria-hidden="true" />
            <h1
              data-testid="payment-success-title"
              className="mt-4 text-3xl font-heading text-fg-primary"
            >
              Payment confirmed
            </h1>
            <p
              data-testid="payment-success-amount"
              className="mt-2 text-fg-secondary text-sm"
            >
              {formatUSD(state.data.amount_cents, state.data.currency)}
              {" \u00b7 "}
              {state.data.mode === "subscription" ? "Subscription" : "One-time"}
              {" \u00b7 "}
              Session <code className="text-xs">{state.data.session_id.slice(0, 24)}&hellip;</code>
            </p>
            <p className="mt-4 text-sm text-fg-primary max-w-md mx-auto">
              Your account access is now updated. If a benefit isn't visible
              right away, refresh the dashboard.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <Link to="/dashboard">
                <Button data-testid="payment-success-go-dashboard-btn">
                  Go to dashboard
                </Button>
              </Link>
              <Link to="/settings">
                <Button
                  variant="outline"
                  data-testid="payment-success-view-access-btn"
                >
                  View your access
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
